import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, AlertCircle, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLUMNS = [
    { key: "todo", label: "To do", color: "bg-brand-lavender/50" },
    { key: "in_progress", label: "In progress", color: "bg-brand-peach/50" },
    { key: "blocked", label: "Blocked", color: "bg-destructive/10" },
    { key: "done", label: "Done", color: "bg-brand-mint/50" },
];

const PRIORITIES = [
    { key: "urgent", label: "Urgent", cls: "bg-destructive text-destructive-foreground" },
    { key: "high", label: "High", cls: "bg-brand-coral text-white" },
    { key: "medium", label: "Medium", cls: "bg-brand-gold text-primary-foreground" },
    { key: "low", label: "Low", cls: "bg-muted text-muted-foreground" },
];

const TaskBoard = () => {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [members, setMembers] = useState([]);
    const [scope, setScope] = useState("mine");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ title: "", description: "", priority: "medium", assigned_to: "", assigned_role: "", status: "todo" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/team/tasks", { params: { scope } });
            setTasks(data.tasks || []);
            if (user?.role === "admin" || user?.role === "super_admin") {
                const m = await api.get("/admin/team").catch(() => ({ data: { members: [] } }));
                setMembers(m.data.members || []);
            }
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

    const create = async () => {
        if (!draft.title) return toast.error("Title required");
        try {
            const payload = { ...draft, assigned_to: draft.assigned_to || null, assigned_role: draft.assigned_role || null };
            await api.post("/team/tasks", payload);
            toast.success("Task created");
            setOpen(false);
            setDraft({ title: "", description: "", priority: "medium", assigned_to: "", assigned_role: "", status: "todo" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const move = async (task, status) => {
        try {
            await api.patch(`/team/tasks/${task.id}`, { status });
            setTasks((xs) => xs.map((x) => x.id === task.id ? { ...x, status } : x));
        } catch { toast.error("Failed"); }
    };

    const remove = async (task) => {
        if (!confirm("Delete this task?")) return;
        try { await api.delete(`/team/tasks/${task.id}`); setTasks((xs) => xs.filter((x) => x.id !== task.id)); } catch {}
    };

    return (
        <div className="p-6 md:p-8" data-testid="team-task-board">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Team Portal</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Task Board</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="rounded-full glass p-1 flex text-xs">
                        {["mine", "role", "all"].map((s) => (
                            <button key={s} onClick={() => setScope(s)} data-testid={`scope-${s}`} className={`px-3 py-1.5 rounded-full font-medium ${scope === s ? "bg-primary text-primary-foreground" : "hover:bg-primary/5"}`}>{s}</button>
                        ))}
                    </div>
                    <Button onClick={() => setOpen(true)} data-testid="new-task-btn" className="rounded-full bg-primary hover:bg-primary/90"><Plus className="w-4 h-4" /> New task</Button>
                </div>
            </div>

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : (
                <div className="grid md:grid-cols-4 gap-4">
                    {COLUMNS.map((col) => {
                        const items = tasks.filter((t) => t.status === col.key);
                        return (
                            <div key={col.key} className={`rounded-3xl p-4 ${col.color} min-h-[300px]`} data-testid={`col-${col.key}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display font-semibold text-sm">{col.label}</h3>
                                    <span className="text-xs bg-white/60 px-2 py-0.5 rounded-full font-semibold">{items.length}</span>
                                </div>
                                <div className="space-y-2">
                                    {items.map((t) => {
                                        const pri = PRIORITIES.find((p) => p.key === t.priority);
                                        return (
                                            <div key={t.id} className="rounded-2xl bg-white p-3 shadow-sm border border-white/60" data-testid={`task-${t.id}`}>
                                                <div className="flex items-center justify-between mb-1">
                                                    {pri && <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${pri.cls}`}>{pri.label}</span>}
                                                    <button onClick={() => remove(t)} className="opacity-0 hover:opacity-100 group-hover:opacity-100 text-destructive"><X className="w-3 h-3" /></button>
                                                </div>
                                                <div className="text-sm font-semibold">{t.title}</div>
                                                {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                                                <div className="mt-3 flex items-center gap-1 flex-wrap">
                                                    {COLUMNS.filter((c) => c.key !== t.status).map((c) => (
                                                        <button key={c.key} onClick={() => move(t, c.key)} className="text-[10px] px-2 py-0.5 rounded-full bg-muted hover:bg-primary/10 text-muted-foreground">→ {c.label}</button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Nothing here yet.</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg rounded-3xl">
                    <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Task title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="task-title" className="rounded-xl h-11" />
                        <textarea placeholder="Details (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full rounded-xl border p-3 text-sm min-h-[80px]" />
                        <div className="grid grid-cols-2 gap-2">
                            <select value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className="rounded-xl border h-11 px-3 text-sm">
                                {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </select>
                            <select value={draft.assigned_role} onChange={(e) => setDraft({ ...draft, assigned_role: e.target.value })} className="rounded-xl border h-11 px-3 text-sm">
                                <option value="">Any role</option>
                                <option value="sales">Sales</option>
                                <option value="support">Support</option>
                                <option value="designer">Designer</option>
                                <option value="production">Production</option>
                            </select>
                        </div>
                        {members.length > 0 && (
                            <select value={draft.assigned_to} onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })} className="w-full rounded-xl border h-11 px-3 text-sm">
                                <option value="">Unassigned</option>
                                {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
                            </select>
                        )}
                        <Button onClick={create} data-testid="task-save" className="w-full rounded-full bg-primary hover:bg-primary/90">Create task</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TaskBoard;
